import React, { useEffect, useState, useRef, useCallback } from "react";
import { fetchWithAuth, getUser, isAuthenticated } from "../../auth/auth";
import { getApiUrl } from "../../utils/getApiUrl";
import { exampleIcon } from "../../utils/config";
import SpeakButton from "../speakButton/speakButton";
import type { UserModel } from "../../type/user";
import type { ExampleAiButtonProps } from "../../type/ai";
import "./exampleAiButton.scss";

const ExampleAiButton: React.FC<ExampleAiButtonProps> = ({ text, langName = "English", langCode }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const [user, setUser] = useState<UserModel | null>(null);
  const [flipDown, setFlipDown] = useState(false);
  const [alignLeft, setAlignLeft] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const instanceIdRef = useRef<string>(Math.random().toString(36).substring(7));

  const checkPosition = useCallback(() => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      //const viewportWidth = window.innerWidth;
      // If less than 200px below the button, flip tooltip above
      setFlipDown(viewportHeight - rect.bottom < 200);
      // If tooltip would overflow left edge (less than 150px from left), align left
      setAlignLeft(rect.left < 150);
    }
  }, []);

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (result && visible) {
      setVisible(false);
      return;
    }
    if (result && !visible) {
      // Close other tooltips before opening this one
      document.dispatchEvent(new CustomEvent('example-tooltip:open', { 
        detail: { instanceId: instanceIdRef.current } 
      }));
      checkPosition();
      setVisible(true);
      return;
    }

    setLoading(true);
    try {
      const response = await fetchWithAuth(getApiUrl('/tools/open-ai'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: `Compose a sentence in ${langName} at ${user?.parameters?.ai_language_level?.value} level, using this word as a key: "${text}". Also return the Polish translation after the ${langName} phrase, separated by ---. Return just the sentence, nothing more.`
        })
      });

      const data = await response.json();

      if (data.content) {
        setResult(data.content);
        // Close other tooltips before opening this one
        document.dispatchEvent(new CustomEvent('example-tooltip:open', { 
          detail: { instanceId: instanceIdRef.current } 
        }));
        checkPosition();
        setVisible(true);
      }
    } catch (err) {
      console.error('Error generating example:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setVisible(false);
    setResult(null);
  };

  useEffect(() => {
    if (isAuthenticated()) {
      const userData = getUser();
      setUser(userData);
    }
  }, []);

  // Listen for other tooltips opening and close this one
  useEffect(() => {
    const handleOtherTooltipOpen = (ev: Event) => {
      const detail = (ev as CustomEvent).detail;
      if (detail?.instanceId !== instanceIdRef.current) {
        setVisible(false);
      }
    };
    document.addEventListener('example-tooltip:open', handleOtherTooltipOpen as EventListener);
    
    return () => {
      document.removeEventListener('example-tooltip:open', handleOtherTooltipOpen as EventListener);
    };
  }, []);

  const getEnglishSentence = (text: string): string => {
    const parts = text.split('---');
    return parts[0].trim().replace(/<[^>]*>/g, '');
  };

  const getPolishSentence = (text: string): string => {
    const parts = text.split('---');
    return parts.length > 1 ? parts[1].trim().replace(/<[^>]*>/g, '') : '';
  };

  return (
    <span className="example-button-wrapper" ref={wrapperRef}>
      <button
        type="button"
        className={`example-button ${loading ? 'loading' : ''} ${visible ? 'active' : ''}`}
        onClick={handleClick}
        title="Generate example sentence"
        disabled={loading}
      >
        {loading ? (
          <span className="example-spinner" />
        ) : (
          <span className="example-icon">{exampleIcon}</span>
        )}
      </button>

      {visible && result && (
        <div
          className={`example-tooltip ${flipDown ? 'flip-down' : ''} ${alignLeft ? 'align-left' : ''}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="example-tooltip-content">
            <div className="example-sentence-level">{`Level: ${user?.parameters?.ai_language_level?.value}`}</div>
            <div className="example-sentence-en">
              {getEnglishSentence(result)}
              <SpeakButton text={getEnglishSentence(result)} lang={langCode} />
            </div>
            <hr />
            {getPolishSentence(result) && (
              <div className="example-sentence-pl">
                {getPolishSentence(result)}
              </div>
            )}
          </div>
          <button className="example-tooltip-close" onClick={handleClose}>×</button>
        </div>
      )}
    </span>
  );
};

export default ExampleAiButton;